import { memo } from "react";
import { Typography } from "@/core/components/ui/typography";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { ActivityEventName } from "./activity-feed.d";

interface ActivityTextProps {
  eventName: ActivityEventName;
  displayName: string;
  spiffedUser: string | null;
  spiffCount: number;
  attributedUser: string | null;
}

export const ActivityText = memo(
  ({
    eventName,
    displayName,
    spiffedUser,
    spiffCount,
    attributedUser,
  }: ActivityTextProps) => {
    switch (eventName) {
      case SPIFF_RULE_EVENTS.signup:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for signing up{" "}
                <span className="font-semibold">{displayName}.</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for signing up{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        if (attributedUser) {
          const verb =
            attributedUser === "Multiple users" || attributedUser === "You"
              ? "were"
              : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{attributedUser}</span> {verb}{" "}
              attributed for signing up{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            <span className="font-semibold">{displayName}</span> signed up.
          </Typography>
        );

      case SPIFF_RULE_EVENTS.referral_creation:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for the referral of{" "}
                <span className="font-semibold">{displayName}.</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for the referral of{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            A customer referred{" "}
            <span className="font-semibold">{displayName}.</span>
            {attributedUser && (
              <>
                {" "}
                Attributed to{" "}
                <span className="font-semibold">{attributedUser}.</span>
              </>
            )}
          </Typography>
        );

      case SPIFF_RULE_EVENTS.referral_completion:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for the completion of a job for{" "}
                <span className="font-semibold">{displayName}.</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for the completion of a job for{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            Job completed for{" "}
            <span className="font-semibold">{displayName}.</span>
            {attributedUser && (
              <>
                {" "}
                Attributed to{" "}
                <span className="font-semibold">{attributedUser}.</span>
              </>
            )}
          </Typography>
        );

      case SPIFF_RULE_EVENTS.five_star_review:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for a review from{" "}
                <span className="font-semibold">{displayName}.</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for a review from{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            <span className="font-semibold">{displayName}</span> left a review.
            {attributedUser && (
              <>
                {" "}
                Attributed to{" "}
                <span className="font-semibold">{attributedUser}.</span>
              </>
            )}
          </Typography>
        );

      case SPIFF_RULE_EVENTS.yard_sign_photo:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for submitting a yard sign photo.
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for submitting a yard sign photo.
            </Typography>
          );
        }
        if (attributedUser) {
          const verb =
            attributedUser === "Multiple users" || attributedUser === "You"
              ? "were"
              : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{attributedUser}</span> {verb}{" "}
              attributed for submitting a yard sign photo.
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">Yard sign photo submitted.</Typography>
        );

      case SPIFF_RULE_EVENTS.pricebook_item_sale:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} people</span>{" "}
                completed a job with{" "}
                <span className="font-semibold">{displayName}</span> in the
                invoice.
              </Typography>
            );
          }
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> completed a
              job with <span className="font-semibold">{displayName}</span> in
              the invoice.
            </Typography>
          );
        }
        if (attributedUser) {
          const verb =
            attributedUser === "Multiple users" || attributedUser === "You"
              ? "were"
              : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{attributedUser}</span> {verb}{" "}
              attributed for completing a job with{" "}
              <span className="font-semibold">{displayName}</span> in the
              invoice.
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            Job completed with{" "}
            <span className="font-semibold">{displayName}</span> in the invoice.
          </Typography>
        );

      case SPIFF_RULE_EVENTS.tech_generated_lead:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for generating a lead:{" "}
                <span className="font-semibold">{displayName}.</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for generating a lead:{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            Tech generated lead:{" "}
            <span className="font-semibold">{displayName}.</span>
          </Typography>
        );

      case SPIFF_RULE_EVENTS.driving_score:
        if (spiffedUser && spiffCount > 0) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for their driving score of{" "}
                <span className="font-semibold">{displayName}.</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for a driving score of{" "}
              <span className="font-semibold">{displayName}.</span>
            </Typography>
          );
        }
        return (
          <Typography variant="bodyW2">
            Driving score of{" "}
            <span className="font-semibold">{displayName}</span> recorded.
          </Typography>
        );

      case SPIFF_RULE_EVENTS.generic:
        // For generic events, displayName contains public_notes from backend
        if (spiffedUser) {
          if (spiffCount > 1) {
            return (
              <Typography variant="bodyW2">
                <span className="font-semibold">{spiffCount} users</span> were
                awarded a spiff for{" "}
                <span className="font-semibold">{displayName}</span>
              </Typography>
            );
          }
          const verb = spiffedUser === "You" ? "were" : "was";
          return (
            <Typography variant="bodyW2">
              <span className="font-semibold">{spiffedUser}</span> {verb}{" "}
              awarded a spiff for{" "}
              <span className="font-semibold">{displayName}</span>
            </Typography>
          );
        }
        // No spiff - just show public_notes with no extra text
        return <Typography variant="bodyW2">{displayName}</Typography>;

      default:
        return (
          <Typography variant="bodyW2">
            <span className="font-semibold">{displayName}</span>
          </Typography>
        );
    }
  },
);

ActivityText.displayName = "ActivityText";
